import React, { useEffect, useRef } from 'react';
import PublicIcon from '@mui/icons-material/Public';
import PobrezaCronicaArgentinaLayer from './PobrezaCronicaArgentinaLayer';
import ZonasInundablesLayer from './ZonasInundablesLayer';
import ZonificacionUrbanaLayer from './ZonificacionUrbanaLayer';
import { mapControlsPanelBackground, mapControlsPanelMaxWidth, mapControlsPanelRight, mapControlsReferencePanelTop, mapControlsReferencePanelMaxHeight } from '../MapControls/MapControls.styles';
import MapToolPanelHeader from '../MapControls/MapToolPanelHeader';

const noop = () => {};

/**
 * Panel flotante de capas de referencia (overlays temáticos).
 *
 * Para agregar una nueva capa:
 *  1. Crear `NombreCapaLayer.js` en este directorio (ver ZonificacionUrbanaLayer como ejemplo).
 *  2. Importarlo aquí y añadir una prop `show* / onToggle*`.
 *  3. Pasar las props desde MapControls → MapPage.
 *
 * Props:
 *  - showPobrezaCronicaArgentina / onTogglePobrezaCronicaArgentina
 *  - showZonasInundables / onToggleZonasInundables
 *  - showZonificacionUrbana / onToggleZonificacionUrbana
 *  - isLoggedIn
 *  - mapZoom
 *  - onClose
 */
const ReferenceLayersPanel = ({
  onClose,
  showPobrezaCronicaArgentina = false,
  onTogglePobrezaCronicaArgentina = noop,
  showZonasInundables = false,
  onToggleZonasInundables = noop,
  showZonificacionUrbana = false,
  onToggleZonificacionUrbana = noop,
  isLoggedIn = false,
  mapZoom = null,
  topOffset = 0,
}) => {
  const layersDisabled = !isLoggedIn;
  const parsedMapZoom = Number.parseFloat(mapZoom);
  const currentMapZoom = Number.isFinite(parsedMapZoom) ? parsedMapZoom : null;
  const panelRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <div
      ref={panelRef}
      style={{
        position: 'absolute',
        right: mapControlsPanelRight,
        top: mapControlsReferencePanelTop(topOffset),
        zIndex: 1100,
        backgroundColor: mapControlsPanelBackground,
        borderRadius: '12px',
        border: '1px solid rgba(255,255,255,0.1)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        display: 'flex',
        flexDirection: 'column',
        width: '360px',
        maxWidth: mapControlsPanelMaxWidth,
        maxHeight: mapControlsReferencePanelMaxHeight(topOffset),
        boxSizing: 'border-box',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        animation: 'refLayerPanelIn 0.15s ease-out',
      }}
    >
      <MapToolPanelHeader
        icon={PublicIcon}
        title="Capas de referencia"
        onClose={onClose}
        sticky
        padded
        backgroundColor={mapControlsPanelBackground}
      />

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '10px 12px 12px',
      }}>
        <PobrezaCronicaArgentinaLayer
          enabled={showPobrezaCronicaArgentina}
          onToggle={onTogglePobrezaCronicaArgentina}
          disabled={layersDisabled}
          mapZoom={currentMapZoom}
        />

        <ZonasInundablesLayer
          enabled={showZonasInundables}
          onToggle={onToggleZonasInundables}
          disabled={layersDisabled}
          mapZoom={currentMapZoom}
        />
        <ZonificacionUrbanaLayer
          enabled={showZonificacionUrbana}
          onToggle={onToggleZonificacionUrbana}
          disabled={layersDisabled}
          mapZoom={currentMapZoom}
        />
      </div>

      <style>{`
        @keyframes refLayerPanelIn {
          from { opacity: 0; transform: translateX(6px) scale(0.97); }
          to   { opacity: 1; transform: translateX(0)  scale(1);    }
        }
      `}</style>
    </div>
  );
};

export default ReferenceLayersPanel;
